import React, { useRef, useState, useLayoutEffect, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';
import { useTranslation } from 'react-i18next';


export const ROW_ACTION_MENU_CLASS =
  'w-48 bg-white rounded-sm shadow-lg border border-gray-200';

export const ROW_ACTION_TRIGGER_CLASS =
  'p-2 min-w-[36px] min-h-[36px] flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors';

export const ROW_ACTION_ITEM_CLASS = {
  default:
    'w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors',
  primary:
    'w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 transition-colors',
  danger:
    'w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors',
  success:
    'w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 transition-colors',
};

const VARIANT_CLASS = ROW_ACTION_ITEM_CLASS;
const MENU_WIDTH = 192;

const assignRef = (ref, node) => {
  if (!ref) return;
  if (typeof ref === 'function') ref(node);
  else ref.current = node;
};

/**
 * Three-dot row actions menu (View / Edit / Delete + optional extras).
 * Menu renders in a portal so it is not clipped by table overflow containers.
 */
const RowActionsMenu = ({
  isOpen,
  onToggle,
  onClose,
  menuRef,
  onView,
  onEdit,
  onDelete,
  viewLabel,
  editLabel,
  deleteLabel,
  extraItems = [],
  leadingItems = [],
  className = 'relative menu-container',
  menuClassName = ROW_ACTION_MENU_CLASS,
  triggerClassName = ROW_ACTION_TRIGGER_CLASS,
}) => {
  const { t } = useTranslation();
  const resolvedViewLabel = viewLabel ?? t('actions.viewDetails');
  const resolvedEditLabel = editLabel ?? t('common.edit');
  const resolvedDeleteLabel = deleteLabel ?? t('common.delete');
  const triggerRef = useRef(null);
  const portalMenuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  const computeMenuStyle = useCallback(() => {
    if (!triggerRef.current) return null;
    if (triggerRef.current.offsetParent === null) return null; // hidden via CSS (e.g. responsive `hidden` class) — skip

    const rect = triggerRef.current.getBoundingClientRect();
    const menuHeight = portalMenuRef.current?.offsetHeight || 132;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < menuHeight + 8 && rect.top > menuHeight + 8;

    let top = openUpward ? rect.top - menuHeight - 4 : rect.bottom + 4;
    let left = rect.right - MENU_WIDTH;

    left = Math.max(8, Math.min(left, window.innerWidth - MENU_WIDTH - 8));
    top = Math.max(8, Math.min(top, window.innerHeight - menuHeight - 8));

    return {
      position: 'fixed',
      top,
      left,
      width: MENU_WIDTH,
      zIndex: 9999,
    };
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuStyle(null);
      return undefined;
    }

    setMenuStyle(computeMenuStyle());
    const frame = requestAnimationFrame(() => {
      setMenuStyle(computeMenuStyle());
    });

    return () => cancelAnimationFrame(frame);
  }, [isOpen, computeMenuStyle, onView, onEdit, onDelete, extraItems.length]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleScrollOrResize = () => setMenuStyle(computeMenuStyle());
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen, computeMenuStyle]);

  useEffect(() => {
    if (!isOpen || !onClose) return undefined;

    const handleClickOutside = (event) => {
      const target = event.target;
      if (
        triggerRef.current?.contains(target) ||
        portalMenuRef.current?.contains(target)
      ) {
        return;
      }
      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const wrapClose = (fn) => () => {
    if (fn) fn();
    if (onClose) onClose();
  };

  const renderItem = (item, idx) => {
    const label = item.icon ? `${item.icon} ${item.label}` : item.label;
    return (
      <button
        key={item.key ?? idx}
        type="button"
        onClick={wrapClose(item.onClick)}
        className={VARIANT_CLASS[item.variant || 'default']}
      >
        {label}
      </button>
    );
  };

  const setContainerRef = (node) => {
    assignRef(menuRef, node);
  };

  const setPortalMenuRef = (node) => {
    portalMenuRef.current = node;
  };

  const portalMenu =
    isOpen &&
    menuStyle &&
    createPortal(
      <div
        ref={setPortalMenuRef}
        className={`menu-container ${menuClassName}`}
        style={menuStyle}
        role="menu"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {leadingItems.map(renderItem)}
        {onEdit && (
          <button type="button" onClick={wrapClose(onEdit)} className={VARIANT_CLASS.default}>
            ✏️ {resolvedEditLabel}
          </button>
        )}
        {onView && (
          <button type="button" onClick={wrapClose(onView)} className={VARIANT_CLASS.default}>
            👁️ {resolvedViewLabel}
          </button>
        )}
        {extraItems.map(renderItem)}
        {onDelete && (
          <button type="button" onClick={wrapClose(onDelete)} className={VARIANT_CLASS.danger}>
            🗑️ {resolvedDeleteLabel}
          </button>
        )}
      </div>,
      document.body
    );

  return (
    <>
      <div className={className} ref={setContainerRef}>
        <button
          ref={triggerRef}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (onToggle) onToggle(e);
          }}
          className={triggerClassName}
          aria-label={t('common.actions')}
          aria-expanded={isOpen}
        >
          <MoreVertical className="w-5 h-5" aria-hidden="true" />
        </button>
      </div>
      {portalMenu}
    </>
  );
};

export default RowActionsMenu;
